import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '@gto/ui/cn'

type SwipeAreaProps = HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode
}

/**
 * Visual grab-handle for top/bottom drawers — communicates that the surface
 * is dismissible. Gesture-driven dismissal itself is handled by the Drawer
 * primitive (set `swipeDirection` on `Drawer.Root`); this component is purely
 * decorative and intentionally does NOT use Base UI's `Drawer.SwipeArea`,
 * which is an invisible edge-listener for swipe-to-OPEN, not swipe-to-dismiss.
 */
export const SwipeArea = ({ children, className, ...props }: SwipeAreaProps) => {
  return (
    <div
      // `min-h-8`: the handle is decorative — on touch the whole sheet is the
      // swipe surface, so the 44px interactive floor does not apply — but it reads
      // as a control, and a 5px bar with no room around it looked like a mistake.
      className={cn('flex min-h-8 shrink-0 select-none items-center justify-center', className)}
      data-tst="drawer-swipe-area"
      {...props}
    >
      {children ?? (
        <span
          aria-hidden
          className="block h-[5px] w-[123px] rounded-lg bg-secondary-main"
        />
      )}
    </div>
  )
}
